import type { CheckInStatus } from '@empower-safety/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface CheckInDto {
  id: string;
  organisation_id: string;
  user_id: string;
  // Worker name joined from the users table. The list endpoint always carries
  // it. Mutation responses set it to null — the dashboard re-fetches the list
  // post-mutation and picks up the populated value from there.
  user_display_name: string | null;
  scheduled_for: string;
  duration_minutes: number;
  status: CheckInStatus;
  completed_at: string | null;
  created_at: string;
}

interface ListQuery {
  status?: CheckInStatus;
  user_id?: string;
  limit?: number;
}

export function useCheckInsList(params: ListQuery = {}) {
  const api = useApi();
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.user_id) qs.set('user_id', params.user_id);
  if (params.limit) qs.set('limit', String(params.limit));
  const search = qs.toString();

  return useQuery({
    queryKey: ['check-ins', params],
    queryFn: () => api<{ check_ins: CheckInDto[] }>(`/check-ins${search ? `?${search}` : ''}`),
    refetchInterval: 15_000,
  });
}

export function useCreateCheckIn() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (durationMinutes: number) =>
      api<CheckInDto>('/check-ins', {
        method: 'POST',
        body: JSON.stringify({ duration_minutes: durationMinutes }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['check-ins'] }),
  });
}

export function useCompleteCheckIn() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<CheckInDto>(`/check-ins/${id}/complete`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['check-ins'] }),
  });
}

export function useExtendCheckIn() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, additionalMinutes }: { id: string; additionalMinutes: number }) =>
      api<CheckInDto>(`/check-ins/${id}/extend`, {
        method: 'POST',
        body: JSON.stringify({ additional_minutes: additionalMinutes }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['check-ins'] }),
  });
}

export function useForceCancelCheckIn() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<CheckInDto>(`/check-ins/${id}/force-cancel`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['check-ins'] }),
  });
}
